import { get } from 'lodash'
import {
  DOCUMENT_STATUS_CHOICES,
  DOCUMENT_STATUS_CONFIG,
  DOCUMENT_TYPES,
  DocumentAttachmentType,
  DocumentMetadata,
  DOCUMENTS_WITH_EFFECTIVE_DATE,
  DOCUMENTS_WITH_EXPIRATION_DATE_OPTIONAL,
  DocumentStatus,
  DocumentType,
  ENCRYPTED_DOCUMENTS,
  EQUIPMENT_FOLDERS,
  FOLDER_NAME_TO_CATEGORY,
  FolderName,
  getDocumentTypeFromSourceAndEquipmentType,
  getTimeFrameChoices,
  GLOBAL_DOCUMENT_CATEGORY_CHOICES,
  HOS_VIOLATIONS,
  PERMITBOOK_DOCUMENTS,
  SAFETY_TOOL_DOCUMENT_STATUS_CHOICES,
} from 'shared/models/document'

import { createDocument, reviewDocument, updateDocument } from '../api/core'
import { Attachment, Document } from '../models/document'

export {
  FolderName,
  DocumentType,
  DocumentAttachmentType,
  DocumentMetadata,
  PERMITBOOK_DOCUMENTS,
  DOCUMENTS_WITH_EFFECTIVE_DATE,
  ENCRYPTED_DOCUMENTS,
  EQUIPMENT_FOLDERS,
  DOCUMENTS_WITH_EXPIRATION_DATE_OPTIONAL,
  FOLDER_NAME_TO_CATEGORY,
  getDocumentTypeFromSourceAndEquipmentType,
  DOCUMENT_STATUS_CHOICES,
  SAFETY_TOOL_DOCUMENT_STATUS_CHOICES,
  HOS_VIOLATIONS,
  getTimeFrameChoices,
  DOCUMENT_TYPES,
  GLOBAL_DOCUMENT_CATEGORY_CHOICES,
}

export const ROOT = FolderName.ROOT
export const DRIVER_INFO = FolderName.DRIVER_INFO
export const TRUCK = FolderName.TRUCK
export const TRAILER = FolderName.TRAILER
export const TEAM = FolderName.TEAM
export const PERMITS_AND_CERTIFICATES = FolderName.PERMITS_AND_CERTIFICATES
export const ALCOHOL_PERMITS = FolderName.ALCOHOL_PERMITS
export const CLOUDTRUCKS_INFORMATION = FolderName.CLOUDTRUCKS_INFORMATION
export const CLOUDTRUCKS_POLICIES = FolderName.CLOUDTRUCKS_POLICIES
export const ELD_INSTRUCTIONS = FolderName.ELD_INSTRUCTIONS
export const V1_0_GLOBAL_DOCUMENTS = FolderName.V1_0_GLOBAL_DOCUMENTS
export const V1_0_ALCOHOL_PERMITS = FolderName.V1_0_ALCOHOL_PERMITS

export const DRIVER_LICENSE = DocumentType.DRIVER_LICENSE
export const MEDICAL_CARD = DocumentType.MEDICAL_CARD
export const INSURANCE_OCCUPATIONAL_ACCIDENT = DocumentType.INSURANCE_OCCUPATIONAL_ACCIDENT
export const TEAM_CT_LEASE_AGREEMENT = DocumentType.TEAM_CT_LEASE_AGREEMENT
export const PASSENGER_AUTHORIZATION = DocumentType.PASSENGER_AUTHORIZATION
export const TRUCK_LAST_DOT_INSPECTION = DocumentType.TRUCK_LAST_DOT_INSPECTION
export const TRAILER_LAST_DOT_INSPECTION = DocumentType.TRAILER_LAST_DOT_INSPECTION
export const TRUCK_OWNERSHIP_AGREEMENT = DocumentType.TRUCK_OWNERSHIP_AGREEMENT
export const TRAILER_OWNERSHIP_AGREEMENT = DocumentType.TRAILER_OWNERSHIP_AGREEMENT
export const TRUCK_INSURANCE_PHYSICAL_DAMAGE = DocumentType.TRUCK_INSURANCE_PHYSICAL_DAMAGE
export const TRAILER_INSURANCE_PHYSICAL_DAMAGE = DocumentType.TRAILER_INSURANCE_PHYSICAL_DAMAGE
export const TRUCK_VEHICLE_REGISTRATION = DocumentType.TRUCK_VEHICLE_REGISTRATION
export const TRAILER_VEHICLE_REGISTRATION = DocumentType.TRAILER_VEHICLE_REGISTRATION
export const TRUCK_INSURANCE_NON_TRUCKING_LIABILITY = DocumentType.TRUCK_INSURANCE_NON_TRUCKING_LIABILITY
export const TRUCK_IFTA_PERMIT = DocumentType.TRUCK_IFTA_PERMIT
export const TRUCK_WEIGHT_MILE_PERMIT_KY = DocumentType.TRUCK_WEIGHT_MILE_PERMIT_KY
export const TRUCK_WEIGHT_MILE_PERMIT_NY = DocumentType.TRUCK_WEIGHT_MILE_PERMIT_NY
export const TRUCK_WEIGHT_MILE_PERMIT_NM = DocumentType.TRUCK_WEIGHT_MILE_PERMIT_NM
export const TRUCK_WEIGHT_MILE_PERMIT_OR = DocumentType.TRUCK_WEIGHT_MILE_PERMIT_OR
export const GLOBAL_DOCUMENT = DocumentType.GLOBAL_DOCUMENT

export const DRIVER_LICENSE_FRONT = DocumentAttachmentType.DRIVER_LICENSE_FRONT
export const DRIVER_LICENSE_BACK = DocumentAttachmentType.DRIVER_LICENSE_BACK

export const DRIVER_LICENSE_ISSUE_DATE = DocumentMetadata.DRIVER_LICENSE_ISSUE_DATE
export const DRIVER_LICENSE_NUMBER = DocumentMetadata.DRIVER_LICENSE_NUMBER
export const DRIVER_LICENSE_STATE = DocumentMetadata.DRIVER_LICENSE_STATE
export const DATE_LAST_DOT_INSPECTION = DocumentMetadata.DATE_LAST_DOT_INSPECTION

export const VALID = DocumentStatus.VALID
export const REVIEW_NEEDED = DocumentStatus.REVIEW_NEEDED

export const DOCUMENT_ACTIONS = {
  UPDATE: {
    id: 'UPDATE',
    api: updateDocument,
    label: 'Update',
  },
  CREATE: {
    id: 'CREATE',
    api: createDocument,
    label: 'Add New',
  },
  APPROVE: {
    id: 'APPROVE',
    api: reviewDocument,
    payload: {
      transition: 'approve',
    },
    label: 'Approve',
  },
  REJECT: {
    id: 'REJECT',
    api: reviewDocument,
    payload: {
      transition: 'reject',
    },
    label: 'Reject',
  },
}

export const DOCUMENT_STATUS: Record<string, any> = {
  EXPIRED: {
    ...DOCUMENT_STATUS_CONFIG.EXPIRED,
    actions: [DOCUMENT_ACTIONS.CREATE],
  },
  MISSING: {
    ...DOCUMENT_STATUS_CONFIG.MISSING,
    actions: [DOCUMENT_ACTIONS.UPDATE],
  },
  MISSING_DATE: {
    ...DOCUMENT_STATUS_CONFIG.MISSING_DATE,
    actions: [DOCUMENT_ACTIONS.UPDATE],
  },
  undefined: {
    ...DOCUMENT_STATUS_CONFIG.undefined,
    actions: [DOCUMENT_ACTIONS.CREATE],
  },
  NOT_UPLOADED: {
    ...DOCUMENT_STATUS_CONFIG.NOT_UPLOADED,
    actions: [DOCUMENT_ACTIONS.CREATE],
  },
  EXPIRING_SOON: {
    ...DOCUMENT_STATUS_CONFIG.EXPIRING_SOON,
    actions: [DOCUMENT_ACTIONS.CREATE],
  },
  EXPIRING: {
    ...DOCUMENT_STATUS_CONFIG.EXPIRING,
    actions: [DOCUMENT_ACTIONS.CREATE],
  },
  VEHICLE_NOT_ASSIGNED: {
    ...DOCUMENT_STATUS_CONFIG.VEHICLE_NOT_ASSIGNED,
    actions: [DOCUMENT_ACTIONS.UPDATE],
  },
  VALID: {
    ...DOCUMENT_STATUS_CONFIG.VALID,
    actions: [DOCUMENT_ACTIONS.UPDATE],
  },
  REVIEW_NEEDED: {
    ...DOCUMENT_STATUS_CONFIG.REVIEW_NEEDED,
    actions: [DOCUMENT_ACTIONS.APPROVE, DOCUMENT_ACTIONS.REJECT, DOCUMENT_ACTIONS.UPDATE],
  },
  REJECTED: {
    ...DOCUMENT_STATUS_CONFIG.REJECTED,
    actions: [DOCUMENT_ACTIONS.CREATE],
  },
}

export function getLicenseFront(attachments: Attachment[]) {
  return attachments?.find(
    (attachment) => attachment.attachment_type === DocumentAttachmentType.DRIVER_LICENSE_FRONT
  )
}

export function getLicenseBack(attachments: Attachment[]) {
  return attachments?.find(
    (attachment) => attachment.attachment_type === DocumentAttachmentType.DRIVER_LICENSE_BACK
  )
}

export function isLastDOTInspection(documentType: DocumentType) {
  return [DocumentType.TRUCK_LAST_DOT_INSPECTION, DocumentType.TRAILER_LAST_DOT_INSPECTION].includes(
    documentType
  )
}

export function getFileAttachmentOnDocument(attachment: Attachment | undefined) {
  let attachment_file = get(attachment, 'attachment_file', null)
  if (!attachment_file) {
    attachment_file = get(attachment, 'attachment_file_encrypted', null)
  }
  return attachment_file
}

export function getFolderName(document: Document) {
  const choice = GLOBAL_DOCUMENT_CATEGORY_CHOICES.find(({ id }) => id === document.folder_name)
  if (choice) {
    return choice.name
  }
  return ''
}

export function getDocumentActions(document: Document, attachment: Attachment | undefined) {
  const attachment_versions = document.document_attachment_versions

  if (!attachment_versions) {
    return [DOCUMENT_ACTIONS.CREATE]
  }

  // if attachment is older of two (first in the list) then no actions
  if (
    attachment_versions.length >= 2 &&
    attachment_versions[0][0].version_number === attachment?.version_number
  ) {
    return []
  }
  const status = attachment ? attachment.status : document.status
  const actions = DOCUMENT_STATUS[status].actions

  if (
    attachment_versions.length === 1 &&
    !actions.includes(DOCUMENT_ACTIONS.CREATE) &&
    status !== REVIEW_NEEDED
  ) {
    return [...actions, DOCUMENT_ACTIONS.CREATE]
  }

  return actions
}

export const getAttachmentFileName = (document: Document) => {
  const documentType = document.document_type
  const documentTypeInfo = DOCUMENT_TYPES.find(({ id }) => id === documentType)
  if (ENCRYPTED_DOCUMENTS.includes(documentType as DocumentType) && documentTypeInfo) {
    return `${documentTypeInfo.attachmentType?.toLowerCase()}_encrypted`
  }
  if (documentTypeInfo) {
    return documentTypeInfo.attachmentType
  }
  return ''
}
